import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SuperAdminLayout from '../components/SuperAdminLayout';

const SuperAdminPaymentGateways = () => {
  const [gateways, setGateways] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'payu',
    config: {},
    isActive: true
  });

  const getConfigFields = (type) => {
    const configs = {
      payu: [
        { key: 'merchantKey', label: 'Merchant Key', type: 'text', required: true },
        { key: 'merchantSalt', label: 'Merchant Salt', type: 'password', required: true },
        { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://secure.payu.in' }
      ],
      icici: [
        { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
        { key: 'accessCode', label: 'Access Code', type: 'text', required: true },
        { key: 'workingKey', label: 'Working Key', type: 'password', required: true },
        { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://secure.ccavenue.com' }
      ],
      idfc: [
        { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
        { key: 'secretKey', label: 'Secret Key', type: 'password', required: true },
        { key: 'apiKey', label: 'API Key', type: 'text', required: true },
        { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://api.idfc.com' }
      ],
      razorpay: [
        { key: 'keyId', label: 'Key ID', type: 'text', required: true },
        { key: 'keySecret', label: 'Key Secret', type: 'password', required: true },
        { key: 'webhookSecret', label: 'Webhook Secret', type: 'password' },
        { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://api.razorpay.com' }
      ]
    };
    return configs[type] || [];
  };

  const updateConfig = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value }
    });
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGateways(response.data.gateways || []);
    } catch (error) {
      console.error('Error fetching gateways:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingGateway) {
        await axios.put(`${API_BASE_URL}/api/payment-gateways/${editingGateway._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/payment-gateways`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchGateways();
      resetForm();
    } catch (error) {
      alert('Error saving gateway: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: 'payu', config: {}, isActive: true });
    setShowAddForm(false);
    setEditingGateway(null);
  };

  const startEdit = (gateway) => {
    setFormData(gateway);
    setEditingGateway(gateway);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gateway?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/api/payment-gateways/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchGateways();
      } catch (error) {
        alert('Error deleting gateway: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  return (
    <SuperAdminLayout>
      <header style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        color: 'white',
        padding: '16px 20px',
        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 4px 0' }}>💳 Payment Gateways</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.85rem' }}>Manage payment gateway configurations</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              border: '1px solid rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            + Add Gateway
          </button>
        </div>
      </header>

      <main style={{ padding: '16px', overflow: 'auto' }}>
        <div className="max-w-6xl mx-auto">

        {showAddForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h3 className="text-xl font-bold mb-4">{editingGateway ? 'Edit Gateway' : 'Add New Gateway'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <input
                  type="text"
                  placeholder="Gateway Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value, config: {}})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="payu">PayU</option>
                  <option value="icici">ICICI CCAvenue</option>
                  <option value="idfc">IDFC Bank</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-800">Gateway Configuration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getConfigFields(formData.type).map(field => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type={field.type}
                        placeholder={field.placeholder || ''}
                        value={formData.config[field.key] || ''}
                        onChange={(e) => updateConfig(field.key, e.target.value)}
                        required={field.required}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  style={{
                    background: '#06b6d4',
                    color: 'white',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#0891b2'}
                  onMouseOut={(e) => e.target.style.background = '#06b6d4'}
                >
                  {editingGateway ? 'Update' : 'Add'} Gateway
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gateways.map(gateway => (
            <div key={gateway._id} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{gateway.name}</h3>
                  <p className="text-gray-600 capitalize font-medium">{gateway.type.toUpperCase()}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    {Object.keys(gateway.config || {}).length} credentials configured
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(gateway)}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-sm transition-colors"
                  >
                    ⚙️ Configure
                  </button>
                  <button
                    onClick={() => handleDelete(gateway._id)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm transition-colors"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-2">Configuration Status:</div>
                <div className="flex flex-wrap gap-2">
                  {getConfigFields(gateway.type).filter(f => f.required).map(field => (
                    <span key={field.key} className={`px-2 py-1 rounded text-xs ${
                      gateway.config?.[field.key] 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {field.label}: {gateway.config?.[field.key] ? '✓' : '✗'}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  gateway.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {gateway.isActive ? '✓ Active' : '✗ Inactive'}
                </span>
                <span className="text-sm text-gray-500">
                  Added {new Date(gateway.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>

        {gateways.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Payment Gateways</h3>
            <p className="text-gray-600 mb-4">Add your first payment gateway to get started</p>
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                background: '#06b6d4',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
              }}
              onMouseOver={(e) => e.target.style.background = '#0891b2'}
              onMouseOut={(e) => e.target.style.background = '#06b6d4'}
            >
              Add Gateway
            </button>
          </div>
        )}
        </div>
      </main>
    </SuperAdminLayout>
  );
};

export default SuperAdminPaymentGateways;